import pick from 'lodash/pick';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { getUserProfile, sendInquiryAlert, transactionLineItems } from '../../util/api';
import * as log from '../../util/log';
import { denormalisedResponseEntities } from '../../util/data';
import { findNextBoundary, getStartOf, monthIdString } from '../../util/dates';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import { getProcess, isBookingProcessAlias } from '../../transactions/transaction';
import { fetchCurrentUser, fetchCurrentUserHasOrdersSuccess, sendNotification } from '../../ducks/user.duck';
import { ASSOCIATED, OWNER } from '../../util/listingsHelpers';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/ListingPage/SET_INITIAL_VALUES';

export const SHOW_LISTING_REQUEST = 'app/ListingPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_ERROR = 'app/ListingPage/SHOW_LISTING_ERROR';

export const FETCH_REVIEWS_REQUEST = 'app/ListingPage/FETCH_REVIEWS_REQUEST';
export const FETCH_REVIEWS_SUCCESS = 'app/ListingPage/FETCH_REVIEWS_SUCCESS';
export const FETCH_REVIEWS_ERROR = 'app/ListingPage/FETCH_REVIEWS_ERROR';

export const FETCH_TIME_SLOTS_REQUEST = 'app/ListingPage/FETCH_TIME_SLOTS_REQUEST';
export const FETCH_TIME_SLOTS_SUCCESS = 'app/ListingPage/FETCH_TIME_SLOTS_SUCCESS';
export const FETCH_TIME_SLOTS_ERROR = 'app/ListingPage/FETCH_TIME_SLOTS_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/ListingPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/ListingPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/ListingPage/FETCH_LINE_ITEMS_ERROR';

export const SEND_INQUIRY_REQUEST = 'app/ListingPage/SEND_INQUIRY_REQUEST';
export const SEND_INQUIRY_SUCCESS = 'app/ListingPage/SEND_INQUIRY_SUCCESS';
export const SEND_INQUIRY_ERROR = 'app/ListingPage/SEND_INQUIRY_ERROR';

export const SEND_INQUIRE_OFFER_REQUEST = 'app/ListingPage/SEND_INQUIRE_OFFER_REQUEST';
export const SEND_INQUIRE_OFFER_SUCCESS = 'app/ListingPage/SEND_INQUIRE_OFFER_SUCCESS';
export const SEND_INQUIRE_OFFER_ERROR = 'app/ListingPage/SEND_INQUIRE_OFFER_ERROR';

export const FETCH_CREATOR_REQUEST = 'app/ListingPage/FETCH_CREATOR_REQUEST';
export const FETCH_CREATOR_SUCCESS = 'app/ListingPage/FETCH_CREATOR_SUCCESS';
export const FETCH_CREATOR_ERROR = 'app/ListingPage/FETCH_CREATOR_ERROR';

export const ADD_AUTHOR_ID = 'app/ListingPage/ADD_AUTHOR_ID';
export const SET_PARENT_IMAGES = 'app/ListingPage/SET_PARENT_IMAGES';

export const FETCH_RELATED_LISTINGS_REQUEST = 'app/ListingPage/FETCH_RELATED_LISTINGS_REQUEST';
export const FETCH_RELATED_LISTINGS_SUCCESS = 'app/ListingPage/FETCH_RELATED_LISTINGS_SUCCESS';
export const FETCH_RELATED_LISTINGS_ERROR = 'app/ListingPage/FETCH_RELATED_LISTINGS_ERROR';

export const FETCH_SIMILAR_LISTINGS_REQUEST = 'app/ListingPage/FETCH_SIMILAR_LISTINGS_REQUEST';
export const FETCH_SIMILAR_LISTINGS_SUCCESS = 'app/ListingPage/FETCH_SIMILAR_LISTINGS_SUCCESS';
export const FETCH_SIMILAR_LISTINGS_ERROR = 'app/ListingPage/FETCH_SIMILAR_LISTINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
  id: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  sendInquiryInProgress: false,
  sendInquiryError: null,
  inquiryModalOpenForListingId: null,
  sendInquiryOfferInProgress: false,
  sendInquiryOfferError: null,
  authorId: null,
  fetchingCreator: false,
  creator: null,
  fetchCreatorError: null,
  parentImages: [],
  relatedListings: [],
  fetchRelatedListingsInProgress: false,
  fetchRelatedListingsError: null,
  similarListings: [],
  fetchSimilarListingsInProgress: false,
  fetchSimilarListingsError: null,
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SHOW_LISTING_REQUEST:
      return { ...state, id: payload.id, showListingError: null };
    case SHOW_LISTING_ERROR:
      return { ...state, showListingError: payload };

    case FETCH_REVIEWS_REQUEST:
      return { ...state, fetchReviewsError: null };
    case FETCH_REVIEWS_SUCCESS:
      return { ...state, reviews: payload };
    case FETCH_REVIEWS_ERROR:
      return { ...state, fetchReviewsError: payload };

    case FETCH_TIME_SLOTS_REQUEST: {
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [payload]: {
          ...state.monthlyTimeSlots[payload],
          fetchTimeSlotsError: null,
          fetchTimeSlotsInProgress: true,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_TIME_SLOTS_SUCCESS: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          timeSlots: payload.timeSlots,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_TIME_SLOTS_ERROR: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          fetchTimeSlotsError: payload.error,
        },
      };
      return { ...state, monthlyTimeSlots };
    }

    case FETCH_LINE_ITEMS_REQUEST:
      return { ...state, fetchLineItemsInProgress: true, fetchLineItemsError: null };
    case FETCH_LINE_ITEMS_SUCCESS:
      return { ...state, fetchLineItemsInProgress: false, lineItems: payload };
    case FETCH_LINE_ITEMS_ERROR:
      return { ...state, fetchLineItemsInProgress: false, fetchLineItemsError: payload };

    case SEND_INQUIRY_REQUEST:
      return { ...state, sendInquiryInProgress: true, sendInquiryError: null };
    case SEND_INQUIRY_SUCCESS:
      return { ...state, sendInquiryInProgress: false };
    case SEND_INQUIRY_ERROR:
      return { ...state, sendInquiryInProgress: false, sendInquiryError: payload };

    case SEND_INQUIRE_OFFER_REQUEST:
      return { ...state, sendInquiryOfferInProgress: true, sendInquiryOfferError: null };
    case SEND_INQUIRE_OFFER_SUCCESS:
      return { ...state, sendInquiryOfferInProgress: false };
    case SEND_INQUIRE_OFFER_ERROR:
      return { ...state, sendInquiryOfferInProgress: false, sendInquiryOfferError: payload };

    case FETCH_CREATOR_REQUEST:
      return { ...state, fetchingCreator: true, fetchCreatorError: null };
    case FETCH_CREATOR_SUCCESS:
      return { ...state, fetchingCreator: false, creator: payload };
    case FETCH_CREATOR_ERROR:
      return { ...state, fetchingCreator: false, fetchCreatorError: payload };

    case ADD_AUTHOR_ID:
      return { ...state, authorId: payload };

    case SET_PARENT_IMAGES:
      return { ...state, parentImages: payload };

    case FETCH_RELATED_LISTINGS_REQUEST:
      return { ...state, fetchRelatedListingsInProgress: true, fetchRelatedListingsError: null };
    case FETCH_RELATED_LISTINGS_SUCCESS:
      return { ...state, fetchRelatedListingsInProgress: false, relatedListings: payload };
    case FETCH_RELATED_LISTINGS_ERROR:
      return { ...state, fetchRelatedListingsInProgress: false, fetchRelatedListingsError: payload };

    case FETCH_SIMILAR_LISTINGS_REQUEST:
      return { ...state, fetchSimilarListingsInProgress: true, fetchSimilarListingsError: null };
    case FETCH_SIMILAR_LISTINGS_SUCCESS:
      return { ...state, fetchSimilarListingsInProgress: false, similarListings: payload };
    case FETCH_SIMILAR_LISTINGS_ERROR:
      return { ...state, fetchSimilarListingsInProgress: false, fetchSimilarListingsError: payload };

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const showListingRequest = id => ({
  type: SHOW_LISTING_REQUEST,
  payload: { id },
});

export const showListingError = e => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});

export const fetchReviewsRequest = () => ({ type: FETCH_REVIEWS_REQUEST });
export const fetchReviewsSuccess = reviews => ({ type: FETCH_REVIEWS_SUCCESS, payload: reviews });
export const fetchReviewsError = error => ({
  type: FETCH_REVIEWS_ERROR,
  error: true,
  payload: error,
});

export const fetchTimeSlotsRequest = monthId => ({
  type: FETCH_TIME_SLOTS_REQUEST,
  payload: monthId,
});
export const fetchTimeSlotsSuccess = (monthId, timeSlots) => ({
  type: FETCH_TIME_SLOTS_SUCCESS,
  payload: { timeSlots, monthId },
});
export const fetchTimeSlotsError = (monthId, error) => ({
  type: FETCH_TIME_SLOTS_ERROR,
  error: true,
  payload: { monthId, error },
});

export const fetchLineItemsRequest = () => ({ type: FETCH_LINE_ITEMS_REQUEST });
export const fetchLineItemsSuccess = lineItems => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: lineItems,
});
export const fetchLineItemsError = error => ({
  type: FETCH_LINE_ITEMS_ERROR,
  error: true,
  payload: error,
});

export const fetchCreatorRequest = () => ({ type: FETCH_CREATOR_REQUEST });
export const fetchCreatorSuccess = payload => ({ type: FETCH_CREATOR_SUCCESS, payload });
export const fetchCreatorError = e => ({ type: FETCH_CREATOR_ERROR, error: true, payload: e });

export const sendInquiryRequest = () => ({ type: SEND_INQUIRY_REQUEST });
export const sendInquirySuccess = () => ({ type: SEND_INQUIRY_SUCCESS });
export const sendInquiryError = e => ({ type: SEND_INQUIRY_ERROR, error: true, payload: e });

export const sendInquiryOfferRequest = () => ({ type: SEND_INQUIRE_OFFER_REQUEST });
export const sendInquiryOfferSuccess = () => ({ type: SEND_INQUIRE_OFFER_SUCCESS });
export const sendInquiryOfferError = e => ({ type: SEND_INQUIRE_OFFER_ERROR, error: true, payload: e });

export const addAuthorId = e => ({ type: ADD_AUTHOR_ID, payload: e });
export const setParentImages = images => ({
  type: SET_PARENT_IMAGES,
  payload: images,
});

export const fetchRelatedListingsRequest = () => ({ type: FETCH_RELATED_LISTINGS_REQUEST });
export const fetchRelatedListingsSuccess = listings => ({
  type: FETCH_RELATED_LISTINGS_SUCCESS,
  payload: listings,
});
export const fetchRelatedListingsError = e => ({
  type: FETCH_RELATED_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const fetchSimilarListingsRequest = () => ({ type: FETCH_SIMILAR_LISTINGS_REQUEST });
export const fetchSimilarListingsSuccess = listings => ({
  type: FETCH_SIMILAR_LISTINGS_SUCCESS,
  payload: listings,
});
export const fetchSimilarListingsError = e => ({
  type: FETCH_SIMILAR_LISTINGS_ERROR,
  error: true,
  payload: e,
});

// Función para prefetch de imágenes de manufacturers
export const prefetchManufacturerImages = (listingId, config) => async (dispatch, getState, sdk) => {
  try {
    // Primero obtener información básica del listing para verificar si es de manufacturer
    const basicParams = {
      id: listingId,
      include: ['images'],
      'fields.listing': ['publicData.isFromManufacturer', 'publicData.manufacturerListingId'],
      'limit.images': 0, // No obtener imágenes del listing actual
    };

    const basicResponse = await sdk.listings.show(basicParams);
    const listing = basicResponse?.data?.data;
    const { isFromManufacturer, manufacturerListingId } = listing?.attributes?.publicData || {};
    const hasImages = listing?.images && listing?.images.length > 0;

    // Si es de manufacturer y no tiene imágenes, prefetch las del padre
    if (isFromManufacturer && manufacturerListingId && !hasImages) {
      const {
        aspectWidth = 1,
        aspectHeight = 1,
        variantPrefix = 'listing-card',
      } = config.layout.listingImage;
      const aspectRatio = aspectHeight / aspectWidth;

      const parentParams = {
        id: manufacturerListingId,
        include: ['images'],
        'fields.image': [
          'variants.scaled-small',
          'variants.scaled-medium',
          'variants.scaled-large',
          'variants.scaled-xlarge',
          `variants.${variantPrefix}`,
          `variants.${variantPrefix}-2x`,
          `variants.${variantPrefix}-4x`,
          `variants.${variantPrefix}-6x`,
          'variants.facebook',
          'variants.twitter',
          'variants.square-small',
          'variants.square-small2x',
        ],
        ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
      };

      // Prefetch en background
      sdk.listings.show(parentParams)
        .then(parentRes => {
          const sanitizeConfig = { listingFields: config?.listing?.listingFields };
          dispatch(addMarketplaceEntities(parentRes, sanitizeConfig));
          
          // Denormaliza las imágenes del padre
          const parentListing = parentRes?.data?.data;
          const parentImageRefs = parentListing?.relationships?.images?.data || [];
          const parentImages = getMarketplaceEntities(getState(), parentImageRefs);
          dispatch(setParentImages(parentImages));
        })
        .catch(error => {
          console.error('Error prefetching parent images:', error);
        });
    }
  } catch (error) {
    console.error('Error in prefetchManufacturerImages:', error);
  }
};

// ================ Thunks ================ //
export const getCreatorData = (creatorId) => (dispatch, getState, sdk) => {
  return getUserProfile({ creatorId }).then(res => {
    dispatch(fetchCreatorSuccess(res?.data?.data));
  }).catch(e => {
    dispatch(fetchCreatorError(storableError(e)));
  });
};

export const fetchRelatedListings = (searchParams, currentListingId, config) => async (dispatch, getState, sdk) => {
  const { isFromManufacturer, manufacturerListingId, authorId } = searchParams;

  // Need either manufacturer or author to search
  if (!isFromManufacturer && !authorId) {
    return;
  }

  dispatch(fetchRelatedListingsRequest());

  try {
    let queryParams = {
      perPage: 6,
      include: ['author', 'author.profileImage', 'images'],
      'fields.image': [
        'variants.landscape-crop',
        'variants.default',
        'variants.listing-card',
        'variants.listing-card2x',
        'variants.scaled-medium',
        'variants.scaled-small',
        'variants.square-small',
        'variants.square-small2x',
        'variants.square-xsmall',
        'variants.square-xsmall2x'
      ],
      'limit.images': 1,
    };

    // Search by manufacturer ID if it's from a manufacturer
    if (isFromManufacturer && manufacturerListingId) {
      queryParams.pub_manufacturerListingId = manufacturerListingId;
    }
    // Otherwise search by author (dealer)
    else if (authorId) {
      queryParams.authorId = authorId;
    }

    const response = await sdk.listings.query(queryParams);

    // Filter out the current listing from the response BEFORE adding to store
    // This prevents overwriting the current listing's full data with limited data
    const currentListingFromResponse = response.data.data.find(
      listing => listing.id.uuid === currentListingId.uuid
    );

    // Get image IDs from the current listing to exclude them from included entities
    const currentListingImageIds = currentListingFromResponse?.relationships?.images?.data?.map(
      img => img.id.uuid
    ) || [];

    const filteredResponse = {
      ...response,
      data: {
        ...response.data,
        data: response.data.data.filter(listing => listing.id.uuid !== currentListingId.uuid),
        // Also filter out current listing's images from included entities
        included: (response.data.included || []).filter(entity => {
          if (entity.type === 'image' && currentListingImageIds.includes(entity.id.uuid)) {
            return false; // Exclude current listing's images
          }
          return true;
        }),
      },
    };

    const sanitizeConfig = { listingFields: config?.listing?.listingFields };
    dispatch(addMarketplaceEntities(filteredResponse, sanitizeConfig));

    // Get denormalized listings
    const listings = denormalisedResponseEntities(filteredResponse);

    // Manually attach profileImage to author objects for the featured listings section
    const included = filteredResponse.data.included || [];
    const processedListings = listings.map(listing => {
      // Find author's profile image from included data
      const author = listing.author;
      if (author && author.relationships?.profileImage?.data?.id?.uuid) {
        const profileImageId = author.relationships.profileImage.data.id.uuid;
        const profileImage = included.find(item =>
          item.type === 'image' && item.id.uuid === profileImageId
        );
        if (profileImage) {
          return {
            ...listing,
            author: {
              ...author,
              profileImage: profileImage
            }
          };
        }
      }
      return listing;
    });

    dispatch(fetchRelatedListingsSuccess(processedListings));
  } catch (e) {
    dispatch(fetchRelatedListingsError(storableError(e)));
    log.error(e, 'fetch-related-listings-failed', {
      searchParams,
      currentListingId: currentListingId.uuid,
    });
  }
};

export const fetchSimilarListings = (subcategory, authorId, currentListingId, config) => async (dispatch, getState, sdk) => {
  if (!subcategory) return;

  dispatch(fetchSimilarListingsRequest());

  try {
    const response = await sdk.listings.query({
      pub_subcategory: subcategory,
      perPage: 6,
      include: ['author', 'author.profileImage', 'images'],
      'fields.image': [
        'variants.listing-card',
        'variants.listing-card2x',
        'variants.scaled-medium',
        'variants.scaled-small',
      ],
      'limit.images': 1,
    });

    const filteredData = response.data.data.filter(
      l => l.id.uuid !== currentListingId.uuid && l.relationships?.author?.data?.id?.uuid !== authorId
    );

    const filteredResponse = { ...response, data: { ...response.data, data: filteredData } };

    const sanitizeConfig = { listingFields: config?.listing?.listingFields };
    dispatch(addMarketplaceEntities(filteredResponse, sanitizeConfig));

    const listings = denormalisedResponseEntities(filteredResponse);
    dispatch(fetchSimilarListingsSuccess(listings));
  } catch (e) {
    dispatch(fetchSimilarListingsError(storableError(e)));
  }
};

export const showListing = (listingId, config, isOwn = false) => async (dispatch, getState, sdk) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  dispatch(showListingRequest(listingId));
  dispatch(fetchCurrentUser());
  
  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.image': [
      // Scaled variants for large images
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',

      // Cropped variants for listing thumbnail images
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,

      // Social media
      'variants.facebook',
      'variants.twitter',

      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
    // Social media image variants
    ...createImageVariantConfig('facebook', 1200, 630 / 1200), // 1200x630 for Facebook/OG
    ...createImageVariantConfig('twitter', 600, 314 / 600), // 600x314 for Twitter
  };

  const show = isOwn ? sdk.ownListings.show(params) : sdk.listings.show(params);

  try {
    const data = await show;
    const listingFields = config?.listing?.listingFields;
    const sanitizeConfig = { listingFields };
    let authorId = null;
    if (data && data?.data?.data?.relationships?.author?.data?.id?.uuid) {
      authorId = data?.data?.data?.relationships?.author?.data?.id?.uuid;
      dispatch(addAuthorId(authorId));
    }

    let { publicData } = data?.data?.data?.attributes || {};
    let { isAssociated = false, creatorType = OWNER, creatorId = authorId } = publicData || {};

    // Skip getCreatorData during SSR as it uses window which is not available
    // The client will fetch this data after hydration
    if (isAssociated && creatorType == ASSOCIATED && creatorId !== authorId && typeof window !== 'undefined') {
      dispatch(getCreatorData(creatorId));
    }

    dispatch(addMarketplaceEntities(data, sanitizeConfig));

    // Manually attach profileImage to author object for SectionThreeColumns
    const included = data?.data?.included || [];
    const listing = data?.data?.data;
    const author = included.find(item =>
      item.type === 'user' && item.id.uuid === listing?.relationships?.author?.data?.id?.uuid
    );

    // Check if the seller is approved - if not, block access to the listing
    if (author && !isOwn) {
      const authorPublicData = author.attributes?.profile?.publicData || {};
      const isApprovedSeller = authorPublicData.approved_seller === true;
      const userType = authorPublicData.userType;
      const isSeller = userType === 'provider' || userType === 'provider-customer';

      if (isSeller && !isApprovedSeller) {
        // Use status 410 (Gone) to indicate seller not approved - will redirect to home
        const error = new Error('Seller not approved');
        error.status = 410;
        error.statusText = 'Gone';
        dispatch(showListingError(storableError(error)));
        throw error;
      }
    }

    if (author && author.relationships?.profileImage?.data?.id?.uuid) {
      const profileImageId = author.relationships.profileImage.data.id.uuid;
      const profileImage = included.find(item =>
        item.type === 'image' && item.id.uuid === profileImageId
      );

      if (profileImage) {
        // Re-add the author with profileImage attached
        const authorWithProfileImage = {
          ...author,
          profileImage: profileImage
        };

        // Update the data to include the enhanced author
        const enhancedData = {
          ...data,
          data: {
            ...data.data,
            included: included.map(item =>
              item.type === 'user' && item.id.uuid === author.id.uuid
                ? authorWithProfileImage
                : item
            )
          }
        };

        // Re-add entities with the enhanced data
        dispatch(addMarketplaceEntities(enhancedData, sanitizeConfig));
      }
    }

    // Optimización: Si es hijo de manufacturer, obtener imágenes del padre en paralelo
    const images = listing?.attributes?.images || [];
    const isFromManufacturer = listing?.attributes?.publicData?.isFromManufacturer;
    const manufacturerListingId = listing?.attributes?.publicData?.manufacturerListingId;
    
    if (isFromManufacturer && manufacturerListingId && images.length === 0) {
      // Obtener imágenes del padre en paralelo para evitar el salto visual
      const parentParams = {
        id: manufacturerListingId,
        include: ['images'],
        'fields.image': [
          'variants.scaled-small',
          'variants.scaled-medium',
          'variants.scaled-large',
          'variants.scaled-xlarge',
          `variants.${variantPrefix}`,
          `variants.${variantPrefix}-2x`,
          `variants.${variantPrefix}-4x`,
          `variants.${variantPrefix}-6x`,
          'variants.facebook',
          'variants.twitter',
          'variants.square-small',
          'variants.square-small2x',
        ],
        ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
      };
      
      // Ejecutar en paralelo para obtener las imágenes inmediatamente
      sdk.listings.show(parentParams)
        .then(parentRes => {
          dispatch(addMarketplaceEntities(parentRes, sanitizeConfig));
          // Denormaliza las imágenes del padre
          const parentListing = parentRes?.data?.data;
          const parentImageRefs = parentListing?.relationships?.images?.data || [];
          const parentImages = getMarketplaceEntities(getState(), parentImageRefs);
          dispatch(setParentImages(parentImages));
        })
        .catch(error => {
          console.error('Error fetching parent images:', error);
          dispatch(setParentImages([]));
        });
    } else {
      dispatch(setParentImages([]));
    }

    return data;
  } catch (e) {
    dispatch(showListingError(storableError(e)));
  }
};

export const fetchReviews = listingId => (dispatch, getState, sdk) => {
  dispatch(fetchReviewsRequest());
  return sdk.reviews
    .query({
      subject_id: listingId,
      type: 'ofProvider',
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const reviews = denormalisedResponseEntities(response);
      dispatch(fetchReviewsSuccess(reviews));
    })
    .catch(e => {
      dispatch(fetchReviewsError(storableError(e)));
    });
};

export const storeView = listingId => (dispatch, getState, sdk) => {
  const { user } = getState();
  const { currentUser } = user;

  const limitRecentlyItems = 10;

  if (currentUser) {
    let listingsArray = currentUser?.attributes?.profile?.privateData?.recentlyViewedListings || [];
    let existsIndex = listingsArray.findIndex(l => l == listingId.uuid);
    if (existsIndex > -1) {
      listingsArray.splice(existsIndex, 1);
    }

    listingsArray.unshift(listingId.uuid);

    listingsArray = listingsArray.slice(0, limitRecentlyItems);

    sdk.currentUser.updateProfile({
        privateData: {
          recentlyViewedListings: listingsArray
        },
      }, {}).then(() => {
        console.log('Listings stored successfully');
      });
  }
};

const timeSlotsRequest = params => (dispatch, getState, sdk) => {
  return sdk.timeslots.query(params);
};

export const fetchTimeSlots = (listingId, start, end, timeZone) => (dispatch, getState, sdk) => {
  const monthId = monthIdString(start, timeZone);

  dispatch(fetchTimeSlotsRequest(monthId));

  // The maximum pagination page size for timeSlots is 500
  const extraParams = {
    perPage: 500,
    page: 1,
  };

  return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
    .then(response => {
      const totalPages = response.data?.meta?.totalPages || 1;
      const remainingPages = [];

      for (let page = 2; page <= totalPages; page++) {
        remainingPages.push(dispatch(timeSlotsRequest({ listingId, start, end, perPage: 500, page })));
      }

      return Promise.all(remainingPages).then(restResponses => {
        const allResponses = [response, ...restResponses];
        const allTimeSlots = allResponses.flatMap(resp => denormalisedResponseEntities(resp));
        dispatch(fetchTimeSlotsSuccess(monthId, allTimeSlots));
      });
    })
    .catch(e => {
      dispatch(fetchTimeSlotsError(monthId, storableError(e)));
    });
};

export const sendInquiry = (listing, message) => (dispatch, getState, sdk) => {
  dispatch(sendInquiryRequest());
  const processAlias = listing?.attributes?.publicData?.transactionProcessAlias;
  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: listing?.id?.uuid,
    });
    dispatch(sendInquiryError(storableError(error)));
    return Promise.reject(error);
  }

  // Validate that this is not a manufacturer listing
  const isManufacturerListing = listing?.attributes?.publicData?.isManufacturer === true;
  if (isManufacturerListing) {
    const error = new Error('Cannot contact manufacturer directly. Please contact a retailer.');
    dispatch(sendInquiryError(storableError(error)));
    return Promise.reject(error);
  }

  const listingId = listing?.id;
  const [processName, alias] = processAlias.split('/');
  const transitions = getProcess(processName)?.transitions;

  const {
    isAssociated = false,
    creatorType,
    creatorId,
  } = listing?.attributes?.publicData || {};

  let associatedParams = isAssociated ? { isAssociated, creatorType, creatorId } : {};
  const protectedData = {...associatedParams};

  const bodyParams = {
    transition: transitions.INQUIRE,
    processAlias,
    params: { listingId, protectedData },
  };
  return sdk.transactions
    .initiate(bodyParams)
    .then(response => {
      const transactionId = response.data.data.id;

      // Send the message to the created transaction
      return sdk.messages.send({ transactionId, content: message }).then(() => {
        dispatch(sendInquirySuccess());
        dispatch(fetchCurrentUserHasOrdersSuccess(true));
        dispatch(sendNotification(transactionId));
        return transactionId;
      });
    })
    .catch(e => {
      dispatch(sendInquiryError(storableError(e)));
      throw e;
    });
};

export const sendInquiryOffer = (listing, offerParams, message) => (dispatch, getState, sdk) => {
  dispatch(sendInquiryOfferRequest());
  const processAlias = listing?.attributes?.publicData?.transactionProcessAlias;
  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: listing?.id?.uuid,
    });
    dispatch(sendInquiryOfferError(storableError(error)));
    return Promise.reject(error);
  }

  // --- Normalización robusta de offer ---
  let normalizedOfferParams = { ...offerParams };
  if (offerParams && offerParams.offer) {
    const offer = offerParams.offer;
    if (offer instanceof sdkTypes.Money) {
      normalizedOfferParams.offer = {
        amount: offer.amount,
        currency: offer.currency,
      };
    } else if (typeof offer === 'object' && offer.amount && offer.currency) {
      normalizedOfferParams.offer = {
        amount: typeof offer.amount === 'object' && offer.amount.toNumber ? offer.amount.toNumber() : Number(offer.amount),
        currency: offer.currency,
      };
    }
    // Si ya es {amount: number, currency: string}, no hago nada
  }
  // --- Fin normalización ---

  const price = listing?.attributes?.price;
  const originalPrice = { amount: price?.amount, currency: price?.currency };
  const originalLabelPrice = { amount: price?.amount / 100, currency: price?.currency };
  const offerLabelPrice = { amount: normalizedOfferParams?.offer?.amount / 100, currency: normalizedOfferParams?.offer?.currency }
  // Validate that this is not a manufacturer listing
  const isManufacturerListing = listing?.attributes?.publicData?.isManufacturer === true;
  if (isManufacturerListing) {
    const error = new Error('Cannot make offers to manufacturer directly. Please contact a retailer.');
    dispatch(sendInquiryOfferError(storableError(error)));
    return Promise.reject(error);
  }

  const listingId = listing?.id;
  const [processName, alias] = processAlias.split('/');
  const transitions = getProcess(processName)?.transitions;

  const {
    isAssociated = false,
    creatorType,
    creatorId,
  } = listing?.attributes?.publicData || {};

  let associatedParams = isAssociated ? { isAssociated, creatorType, creatorId } : {};
  // Solo hago spread si es un objeto
  const protectedData = {
    ...(typeof normalizedOfferParams === 'object' && normalizedOfferParams !== null ? normalizedOfferParams : {}),
    offerLabelPrice,
    originalPrice,
    originalLabelPrice,
    ...associatedParams
  };

  const bodyParams = {
    transition: transitions.INQUIRE_OFFER,
    processAlias,
    params: { listingId, protectedData },
  };

  // LOG TEMPORAL PARA DEPURACIÓN
  // eslint-disable-next-line no-console
  console.log('DEBUG sendInquiryOffer bodyParams:', JSON.stringify(bodyParams, null, 2));

  return sdk.transactions
    .initiate(bodyParams)
    .then(response => {
      const transactionId = response.data.data.id;
      plausible('sendOffer', {props: { listingId: listingId.uuid }});
      sendInquiryAlert({ listingId: listingId.uuid });

      // Send the message to the created transaction
      return sdk.messages.send({ transactionId, content: message }).then(() => {
        dispatch(sendInquiryOfferSuccess());
        dispatch(fetchCurrentUserHasOrdersSuccess(true));
        dispatch(sendNotification(transactionId));
        return transactionId;
      });
    })
    .catch(e => {
      dispatch(sendInquiryOfferError(storableError(e)));
      throw e;
    });
}

// Helper function for loadData call.
const fetchMonthlyTimeSlots = (dispatch, listing) => {
  const hasWindow = typeof window !== 'undefined';
  const attributes = listing.attributes;
  // Listing could be ownListing entity too, so we just check if attributes key exists
  const hasTimeZone =
    attributes && attributes.availabilityPlan && attributes.availabilityPlan.timezone;

  // Fetch time-zones on client side only.
  if (hasWindow && listing.id && hasTimeZone) {
    const tz = listing.attributes.availabilityPlan.timezone;
    const unitType = attributes?.publicData?.unitType;
    const timeUnit = unitType === 'hour' ? 'hour' : 'day';
    const nextBoundary = findNextBoundary(new Date(), timeUnit, tz);

    const nextMonth = getStartOf(nextBoundary, 'month', tz, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', tz, 1, 'months');

    return Promise.all([
      dispatch(fetchTimeSlots(listing.id, nextBoundary, nextMonth, tz)),
      dispatch(fetchTimeSlots(listing.id, nextMonth, nextAfterNextMonth, tz)),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => dispatch => {
  dispatch(fetchLineItemsRequest());
  transactionLineItems({ orderData, listingId, isOwnListing })
    .then(response => {
      const lineItems = response.data;
      dispatch(fetchLineItemsSuccess(lineItems));
    })
    .catch(e => {
      dispatch(fetchLineItemsError(storableError(e)));
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        orderData,
      });
    });
};

export const loadData = (params, search, config) => dispatch => {
  const listingId = new UUID(params.id);

  // Clear old line-items
  dispatch(setInitialValues({ lineItems: null }));

  const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];
  if (ownListingVariants.includes(params.variant)) {
    return dispatch(showListing(listingId, config, true));
  }

  // Primero obtenemos el listing, luego reviews del autor
  return dispatch(showListing(listingId, config)).then(data => {
    const listing = data?.data?.data;
    const authorId = listing?.relationships?.author?.data?.id?.uuid;
    const promises = [];

    if (authorId) {
      promises.push(dispatch(fetchReviews(authorId)));
    }
    if (listing) {
      promises.push(fetchMonthlyTimeSlots(dispatch, listing));
    }

    return Promise.all(promises);
  });
};
